import { useState, useEffect, useCallback } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Users,
  Download,
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  MapPin,
  Loader2,
} from "lucide-react";
import { eventService } from "@project/services/admin/event.service";
import { eventCategoryService } from "@project/services/admin/event-category.service";
import type { Event, EventCategory } from "@project/types/event";
import { AdminEventFormDialog } from "@project/admin/components/admin-event-form-dialog";
import { AdminEventRegistrationsDialog } from "@project/admin/components/admin-event-registrations-dialog";
import { projectToast } from "@project/lib/toast";

export default function EventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters State
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Dialogs State
  const [isEventFormOpen, setIsEventFormOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<Event | null>(null);
  const [viewingEvent, setViewingEvent] = useState<Event | null>(null);
  const [isRegistrationsOpen, setIsRegistrationsOpen] = useState(false);
  const [selectedEventForRegistrations, setSelectedEventForRegistrations] =
    useState<Event | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams: any = {
        page: 1,
        page_size: 100,
        search: search.trim() || undefined,
      };
      if (selectedCategory !== "all") {
        queryParams.category = selectedCategory;
      }
      if (selectedStatus !== "all") {
        queryParams.status = selectedStatus;
      }

      const res = await eventService.listPage(queryParams);
      setEvents(res.data || []);
    } catch (err) {
      projectToast.error("Failed to load events");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory, selectedStatus]);

  const fetchCategories = async () => {
    try {
      const res = await eventCategoryService.listPage({
        page: 1,
        page_size: 100,
      });
      setCategories(res.data ?? []);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreateEvent = () => {
    setEventToEdit(null);
    setIsEventFormOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setEventToEdit(event);
    setIsEventFormOpen(true);
  };

  const handleSaveEvent = async (eventData: any) => {
    try {
      if (eventToEdit) {
        await eventService.update(eventToEdit.id, eventData);
        projectToast.success("Event updated successfully");
      } else {
        await eventService.create(eventData);
        projectToast.success("Event published successfully");
      }
      fetchEvents();
    } catch (err) {
      projectToast.error("Failed to save event");
      console.error(err);
    }
  };

  const handleDeleteEvent = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this event?")) {
      return;
    }
    try {
      await eventService.delete(id);
      projectToast.success("Event deleted successfully");
      fetchEvents();
    } catch (err) {
      projectToast.error("Failed to delete event");
      console.error(err);
    }
  };

  const handleViewRegistrations = (event: Event) => {
    setSelectedEventForRegistrations(event);
    setIsRegistrationsOpen(true);
  };

  const exportCSV = () => {
    const headers = [
      "Event Title,Category,Date,Start Time,End Time,Venue,Capacity,Status,Address,Start Date,End Date\n",
    ];
    const rows = events.map(e => {
      const catName =
        categories.find(c => String(c.id) === String(e.category))
          ?.category_name ?? "Unknown";
      return `"${e.event_title}","${catName}","${e.date}","${e.start_time}","${e.end_time}","${e.venue}","${e.max_seat_capacity}","${e.status}","${e.address}","${e.start_date}","${e.end_date}"`;
    });
    const blob = new Blob([...headers, rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `events-list.csv`;
    a.click();
  };

  return (
    <AdminShell
      title="Events Management"
      subtitle="Create, edit, and organize old age home community events"
    >
      {/* Stat Cards Grid */}
      {/* <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Upcoming Events"
          value={publishedLiveCount}
          icon={<CalendarDays className="h-5 w-5" />}
          accent="from-primary/90 to-primary"
          sub="Published live"
        />
        <StatCard
          label="Total Registrations"
          value={totalRegistrations}
          icon={<Users className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub="Confirmed attendees"
        />
        <StatCard
          label="Total Seat Capacity"
          value={totalSeatCapacity}
          icon={<Ticket className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub="Available seats"
        />
        <StatCard
          label="Active Categories"
          value={activeCategoriesCount}
          icon={<FolderKanban className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
          sub="Dharma, Health, Festivals"
        />
      </div> */}

      {/* Main Section */}
      <div className="mt-6">
        <SectionCard
          title="Events & Registrations Control"
          subtitle="Full CRUD control over events, registration forms, and attendees"
        >
          {/* Filters, Add and Export Row */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-[300px] flex-1 flex-wrap items-center gap-3">
              {/* Search Bar */}
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search events or venue..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 text-xs"
                />
              </div>

              {/* Category Filter */}
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger className="w-40 text-xs">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.category_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-36 text-xs">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Published">Published</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportCSV}
                className="h-9 gap-1 text-xs"
              >
                <Download className="h-3.5 w-3.5" /> Export CSV
              </Button>
              <Button
                size="sm"
                onClick={handleCreateEvent}
                className="h-9 gap-1 bg-primary text-xs font-semibold text-primary-foreground"
              >
                <Plus className="h-4 w-4" /> Add New Event
              </Button>
            </div>
          </div>

          {/* Events Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/40">
                    <TableHead className="text-xs">
                      Event Title & Category
                    </TableHead>
                    <TableHead className="text-xs">Date & Time</TableHead>
                    <TableHead className="text-xs">Venue Location</TableHead>
                    <TableHead className="text-xs">Registrations</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-right text-xs">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="py-12 text-center text-xs text-muted-foreground"
                      >
                        No events found matching the criteria.
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.map(e => {
                      const catName =
                        categories.find(
                          c => String(c.id) === String(e.category)
                        )?.category_name ?? "Uncategorized";
                      const displayTime = e.start_time
                        ? e.start_time.slice(0, 5)
                        : "";
                      return (
                        <TableRow key={e.id} className="text-xs">
                          <TableCell className="py-3">
                            <div className="font-semibold text-foreground">
                              {e.event_title}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              {catName}
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <div className="font-medium text-foreground">
                              {e.date}
                            </div>
                            {displayTime && (
                              <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                                <Clock className="h-3 w-3 text-gold" />{" "}
                                {displayTime}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate py-3">
                            <div className="font-medium text-foreground">
                              {e.venue}
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <div className="font-semibold text-foreground">
                              {e.registrations_count ?? 35}
                            </div>
                            <div className="mt-0.5 text-[10px] text-muted-foreground">
                              / {e.max_seat_capacity || 150} Seats
                            </div>
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge
                              className={`rounded-full border border-transparent px-2 py-0.5 text-[10px] font-bold lowercase ${
                                e.status === "Published"
                                  ? "border-rose-100 bg-rose-50 text-rose-700"
                                  : e.status === "Draft"
                                    ? "border-amber-100 bg-amber-50 text-amber-700"
                                    : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {e.status === "Published"
                                ? "upcoming"
                                : e.status.toLowerCase()}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewRegistrations(e)}
                                className="h-8 gap-1 border border-border px-2.5 text-[11px]"
                              >
                                <Users className="h-3.5 w-3.5 text-primary" />{" "}
                                Registrations
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setViewingEvent(e)}
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEditEvent(e)}
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDeleteEvent(e.id)}
                                className="h-8 w-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Admin Event Add/Edit Form Dialog */}
      <AdminEventFormDialog
        open={isEventFormOpen}
        onOpenChange={setIsEventFormOpen}
        eventToEdit={eventToEdit}
        onSave={handleSaveEvent}
      />

      {/* Admin Event Registrations Viewer Dialog */}
      {selectedEventForRegistrations && (
        <AdminEventRegistrationsDialog
          open={isRegistrationsOpen}
          onOpenChange={setIsRegistrationsOpen}
          event={{
            id: selectedEventForRegistrations.id,
            title: selectedEventForRegistrations.event_title,
          }}
        />
      )}

      {/* View Event Details Dialog */}
      <Dialog
        open={!!viewingEvent}
        onOpenChange={open => !open && setViewingEvent(null)}
      >
        <DialogContent className="max-w-xl p-6 sm:rounded-2xl">
          {viewingEvent && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between pr-4">
                  <Badge
                    variant="secondary"
                    className="bg-rose-100 text-[10px] font-bold uppercase tracking-wider text-rose-700"
                  >
                    {categories.find(
                      c => String(c.id) === String(viewingEvent.category)
                    )?.category_name ?? "Event"}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="text-xs font-semibold capitalize"
                  >
                    {viewingEvent.status}
                  </Badge>
                </div>
                <DialogTitle className="mt-2 text-xl font-bold text-foreground">
                  {viewingEvent.event_title}
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 space-y-4">
                {viewingEvent.image && (
                  <img
                    src={viewingEvent.image}
                    alt={viewingEvent.event_title}
                    className="h-48 w-full rounded-xl border border-border object-cover"
                  />
                )}

                <div className="bg-secondary/30 border-border/50 grid grid-cols-2 gap-4 rounded-xl border p-3 text-xs">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-[10px] font-bold uppercase text-muted-foreground">
                        Date
                      </p>
                      <p className="font-medium text-foreground">
                        {viewingEvent.date}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-[10px] font-bold uppercase text-muted-foreground">
                        Time
                      </p>
                      <p className="font-medium text-foreground">
                        {viewingEvent.start_time.slice(0, 5)} -{" "}
                        {viewingEvent.end_time.slice(0, 5)}
                      </p>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-[10px] font-bold uppercase text-muted-foreground">
                        Venue
                      </p>
                      <p className="font-medium text-foreground">
                        {viewingEvent.venue}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {viewingEvent.address}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-[10px] font-bold uppercase text-muted-foreground">
                        Seat Capacity
                      </p>
                      <p className="font-medium text-foreground">
                        {viewingEvent.max_seat_capacity} Seats
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-[10px] font-bold uppercase text-muted-foreground">
                        Duration
                      </p>
                      <p className="font-medium text-foreground">
                        {viewingEvent.start_date} to {viewingEvent.end_date}
                      </p>
                    </div>
                  </div>
                </div>

                {viewingEvent.short_summary && (
                  <div>
                    <h4 className="text-xs font-bold text-foreground">
                      Summary
                    </h4>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {viewingEvent.short_summary}
                    </p>
                  </div>
                )}

                {viewingEvent.description && (
                  <div>
                    <h4 className="text-xs font-bold text-foreground">
                      Description
                    </h4>
                    <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                      {viewingEvent.description}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
